import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '@components/forms/Button';
import StatusChip from '@components/widgets/StatusChip';
import { ReactComponent as PlusIcon } from '@assets/icons/plus_icon.svg';
import { useAuthStore } from '@stores';
import { getCurrentUser } from '@utils/userUtils';
import {
  post,
  GET_BROKER_ORGANISATION,
  LIST_BROKER_USERS,
  UPDATE_BROKER_USER,
  REMOVE_BROKER_USER,
  UPDATE_BROKER_ORGANISATION
} from '@services';
import MembersTab from './components/MembersTab';
import InvitesTab from './components/InvitesTab';
import InviteMemberModal from './components/InviteMemberModal';
import SettingsTab from './components/SettingsTab';
import {
  isPendingInvite,
  mapBrokerOrg,
  toApiRole,
  toBrokerOrgInvite,
  type ApiBrokerOrganisation,
  type ApiBrokerUser
} from './mapBrokerOrg';
import type { BrokerOrg, BrokerOrgMember, BrokerOrgMemberRole } from '@types';

type BrokerMembership = {
  brokerOrganisationId?: string;
  roles?: string[];
};

const BrokerOrg = () => {
  const navigate = useNavigate();
  const { setBrokerOrgStatus, setBrokerOrgName } = useAuthStore();
  const [activeTab, setActiveTab] = useState<'members' | 'invites' | 'settings'>('members');
  const [org, setOrg] = useState<BrokerOrg | null>(null);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [underReview, setUnderReview] = useState(false);
  const currentUserId = localStorage.getItem('userId') ?? '';

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const auth = useAuthStore.getState();
      if (!auth.isBroker()) {
        navigate('/', { replace: true });
        return;
      }

      const current = await getCurrentUser();
      const brokers = ((current as { brokers?: BrokerMembership[] } | null)?.brokers
        ?? auth.brokers
        ?? []) as BrokerMembership[];
      const membership = brokers.find(
        (broker: { brokerOrganisationId?: string }) => broker.brokerOrganisationId
      );

      if (!membership?.brokerOrganisationId) {
        if (!cancelled && auth.brokerOrgStatus !== 'NONE') {
          setUnderReview(true);
          setIsLoading(false);
          return;
        }
        navigate('/broker-org/register', { replace: true });
        return;
      }

      try {
        const orgResponse = await post(GET_BROKER_ORGANISATION, {
          organisationId: membership.brokerOrganisationId
        });
        let users: ApiBrokerUser[] = [];
        try {
          const usersResponse = await post(LIST_BROKER_USERS, {
            organisationId: membership.brokerOrganisationId
          });
          users = usersResponse?.brokerUsers ?? [];
        } catch (error) {
          console.error('Error loading broker organisation members:', error);
        }
        if (cancelled) return;

        const mapped = mapBrokerOrg(orgResponse.broker as ApiBrokerOrganisation, users);
        setOrg(mapped);
        setIsOrgAdmin(membership.roles?.includes('ADMIN') ?? false);
        setBrokerOrgName(mapped.name);
        setBrokerOrgStatus(mapped.status === 'ACTIVE' ? 'ACTIVE' : 'PENDING');
      } catch (error) {
        console.error('Error loading broker organisation:', error);
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [navigate, setBrokerOrgName, setBrokerOrgStatus]);

  const handleChangeRole = async (userId: string, role: BrokerOrgMemberRole) => {
    if (!org) return;
    try {
      await post(UPDATE_BROKER_USER, {
        brokerOrganisationId: org.brokerOrgId,
        userId,
        roles: [toApiRole(role)]
      });
      setOrg((prev) =>
        prev
          ? { ...prev, members: prev.members.map((member) => member.userId === userId ? { ...member, role } : member) }
          : prev
      );
    } catch (error) {
      console.error('Error updating broker organisation member role:', error);
    }
  };

  const removeUser = async (userId: string) => {
    if (!org) return;
    try {
      await post(REMOVE_BROKER_USER, {
        brokerOrganisationId: org.brokerOrgId,
        userId
      });
      setOrg((prev) =>
        prev
          ? {
              ...prev,
              members: prev.members.filter((member) => member.userId !== userId),
              pendingInvites: prev.pendingInvites.filter((invite) => invite.inviteId !== userId)
            }
          : prev
      );
    } catch (error) {
      console.error('Error removing broker organisation member:', error);
    }
  };

  const handleInviteSent = (member: BrokerOrgMember, user: ApiBrokerUser) => {
    setOrg((prev) => {
      if (!prev) return prev;
      const pendingInvites = isPendingInvite(user)
        ? [...prev.pendingInvites, toBrokerOrgInvite(user)]
        : prev.pendingInvites;
      return { ...prev, members: [...prev.members, member], pendingInvites };
    });
  };

  const handleAutoJoinChange = async (enabled: boolean) => {
    if (!org) return;
    await post(UPDATE_BROKER_ORGANISATION, {
      organisationId: org.brokerOrgId,
      autoJoin: enabled
    });
    setOrg((prev) => prev ? { ...prev, autoJoin: enabled } : prev);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-grey-600 text-sm">Loading organisation...</p>
      </div>
    );
  }

  if (underReview) {
    return (
      <div className="py-6 px-2">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-xl font-bold text-grey-900">Broker Organisation</h1>
          <StatusChip variant="warning" label="Under Review" />
        </div>
        <p className="text-sm text-grey-500">We're verifying your details. You'll be notified once approved.</p>
      </div>
    );
  }

  if (loadError || !org) {
    return (
      <div className="py-6 px-2">
        <p className="text-sm text-grey-600">The broker organisation could not be loaded.</p>
      </div>
    );
  }

  const pendingCount = org.pendingInvites.filter((invite) => invite.status === 'PENDING').length;

  return (
    <div className="py-6 px-2">
      <div className="border-b border-grey-200 pb-0 mb-0">
        <div className="flex items-center justify-between px-1 mb-4">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-grey-900">{org.name}</h1>
            <StatusChip variant={org.status === 'ACTIVE' ? 'success' : 'warning'} label={org.status === 'ACTIVE' ? 'Active' : 'Under Review'} />
            <span className="text-sm text-grey-400">
              {org.emailDomain ? `@${org.emailDomain}` : ''}
              {org.emailDomain && org.abn ? ' · ' : ''}
              {org.abn ? `ABN ${org.abn}` : ''}
            </span>
          </div>
          {isOrgAdmin && org.status === 'ACTIVE' && (
            <Button className="capitalize bg-primary flex items-center text-sm h-10 gap-2 py-2.5 px-4" onClick={() => setShowInviteModal(true)}>
              <PlusIcon />
              Invite Member
            </Button>
          )}
        </div>
        <div className="flex gap-0 px-1">
          <button
            className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'members' ? 'border-secondary text-grey-900' : 'border-transparent text-grey-500 hover:text-grey-700'}`}
            onClick={() => setActiveTab('members')}>
            Members
            <span className="ml-2 bg-grey-100 text-grey-500 text-xs px-1.5 py-0.5 rounded-full">{org.members.length}</span>
          </button>
          <button
            className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'invites' ? 'border-secondary text-grey-900' : 'border-transparent text-grey-500 hover:text-grey-700'}`}
            onClick={() => setActiveTab('invites')}>
            Pending Invites
            {pendingCount > 0 && <span className="ml-2 text-xs px-1.5 py-0.5 rounded-full" style={{ background: 'rgba(111,24,119,0.07)', color: '#6F1877', border: '1px solid rgba(111,24,119,0.2)' }}>{pendingCount}</span>}
          </button>
          {isOrgAdmin && (
            <button
              className={`px-5 py-3 text-sm font-semibold border-b-2 transition-colors ${activeTab === 'settings' ? 'border-secondary text-grey-900' : 'border-transparent text-grey-500 hover:text-grey-700'}`}
              onClick={() => setActiveTab('settings')}>
              Settings
            </button>
          )}
        </div>
      </div>
      <div className="pt-2">
        {activeTab === 'members' && (
          <MembersTab members={org.members} currentUserId={currentUserId} isAdmin={isOrgAdmin} onChangeRole={handleChangeRole} onRemove={removeUser} onResendInvite={() => {}} />
        )}
        {activeTab === 'invites' && (
          <InvitesTab invites={org.pendingInvites} isAdmin={isOrgAdmin} onCancel={removeUser} onResend={() => {}} />
        )}
        {activeTab === 'settings' && (
          <SettingsTab
            emailDomain={org.emailDomain}
            autoJoin={org.autoJoin}
            existingDomainUserCount={org.existingDomainUserCount}
            isAdmin={isOrgAdmin}
            onAutoJoinChange={handleAutoJoinChange}
          />
        )}
      </div>
      <InviteMemberModal
        open={showInviteModal}
        orgDomain={org.emailDomain}
        organisationId={org.brokerOrgId}
        onClose={() => setShowInviteModal(false)}
        onInviteSent={handleInviteSent}
      />
    </div>
  );
};

export default BrokerOrg;
